import JackpotComponent from "ags/components/jackpot-component";
import TrailDisplay from "ags/components/trail-display";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import CanvasManager, { Orientation } from "slotworks/graphics/canvas/canvas-manager";
import GraphicsManager from "slotworks/graphics/graphics-manager";
import Layers from "slotworks/graphics/layers/layers";
import Contract from "slotworks/utils/contracts/contract";
import Point from "slotworks/utils/geom/point";
import Rectangle from "slotworks/utils/geom/rectangle";
import { sequence } from "slotworks/utils/race-utils";
import Sprite = PIXI.Sprite;
import * as TweenJS from "slotworks/utils/tween";

export class FireworksComponent implements TrailDisplay {

    private static GLOW_TIME: number = 200;

    public landDelay: number = 500;

    protected layer: Layers;
    private orientation: Orientation;

    private currentLevel: number = 0;

    private currentPosition: Rectangle;
    private landscapePosition: Rectangle;
    private portraitPosition: Rectangle;

    private fireworks: AnimatedSprite;

    private portraitGlow: Sprite;
    private landscapeGlow: Sprite;

    public setup() {
        this.layer = JackpotComponent.fireworksLayer;

        if (!this.layer) {
            return;
        }

        this.landscapePosition = this.layer.getPosition("landscape_sparks");
        this.portraitPosition = this.layer.getPosition("portrait_sparks");

        this.portraitGlow = this.layer.getSprite("fireworks_glow_portrait");
        this.landscapeGlow = this.layer.getSprite("fireworks_glow_landscape");

        if (!this.portraitGlow) {
            return;
        }

        this.portraitGlow.alpha = 0;
        this.landscapeGlow.alpha = 0;

        CanvasManager.onOrientationChange.add((orientation: Orientation) => this.switchOrientation(orientation));

        this.switchOrientation(CanvasManager.orientation);
    }

    public setLevel(level: number) {
        this.currentLevel = Number.isInteger(level) ? level : this.currentLevel;

        this.showCorrectLevel();
    }

    public reset() {
        this.setLevel(0);
    }

    public upgrade(upgradeLevel: number) {

        sequence([
            () => this.glowIn(),
            () => Contract.wrap(() => {
                this.setLevel(Math.min(5, upgradeLevel));
            }),
            () => Contract.getTimeoutContract(400),
            () => this.glowOut()
        ]).execute();
    }

    public getPosition(orientation: Orientation) {
        const position = orientation === Orientation.PORTRAIT ? this.portraitPosition : this.landscapePosition;

        const x = position.x + position.width / 2;
        const y = position.y + position.height / 2;
        return new Point(x, y);
    }

    public sparkLanded() {
        // For override
    }

    protected switchOrientation(orientation: Orientation) {
        this.orientation = orientation;

        switch (orientation) {
            case Orientation.LANDSCAPE:
            default:
                this.currentPosition = this.landscapePosition;
                this.setVisible(true, orientation.toString());
                this.setVisible(false, Orientation.PORTRAIT.toString());
                this.portraitGlow.visible = false;
                this.landscapeGlow.visible = true;
                break;
            case Orientation.PORTRAIT:
                this.currentPosition = this.portraitPosition;
                this.setVisible(true, orientation.toString());
                this.setVisible(false, Orientation.LANDSCAPE.toString());
                this.portraitGlow.visible = true;
                this.landscapeGlow.visible = false;
                break;
        }
    }

    private glowIn() {
        return new Contract<any>((resolve) => {
            this.layer.add(this.portraitGlow);
            this.layer.add(this.landscapeGlow);

            new TweenJS.Tween(this.portraitGlow)
                .to({ alpha: 1 }, FireworksComponent.GLOW_TIME)
                .start();

            new TweenJS.Tween(this.landscapeGlow)
                .to({ alpha: 1 }, FireworksComponent.GLOW_TIME)
                .onComplete(() => {
                    resolve(null);
                })
                .start();
        });
    }

    private glowOut() {
        return new Contract<any>((resolve) => {
            new TweenJS.Tween(this.portraitGlow)
                .to({ alpha: 0 }, FireworksComponent.GLOW_TIME)
                .start();

            new TweenJS.Tween(this.landscapeGlow)
                .to({ alpha: 0 }, FireworksComponent.GLOW_TIME)
                .onComplete(() => {
                    resolve(null);
                })
                .start();
        });
    }

    private setVisible(visible: boolean, orientation: string) {
        const fireworksBg = this.layer.getSprite("fireworks_bg_" + orientation);
        if (fireworksBg) {
            fireworksBg.visible = visible;
        }

        this.showCorrectLevel();
    }

    private showCorrectLevel() {
        if (this.fireworks) {
            this.layer.remove(this.fireworks);
            this.fireworks.destroy();
        }

        if (this.currentLevel <= 0) {
            return;
        }

        this.fireworks = GraphicsManager.createAnimation("fireworks_sparks_" + this.currentLevel.toString());

        if (!this.fireworks) {
            return;
        }

        this.layer.add(this.fireworks);
        this.layer.add(this.portraitGlow);
        this.layer.add(this.landscapeGlow);
        this.fireworks.visible = true;

        if (this.orientation === Orientation.LANDSCAPE) {
            this.fireworks.anchor.set(0.5, 0.5);
            this.fireworks.rotation = 0.5 * Math.PI;
            this.fireworks.width = this.currentPosition.height;
            this.fireworks.height = this.currentPosition.width;

            this.fireworks.x = this.currentPosition.x + this.fireworks.height * 0.5;
            this.fireworks.y = this.currentPosition.y + this.fireworks.width * 0.5;
        } else {
            this.fireworks.width = this.currentPosition.width;
            this.fireworks.height = this.currentPosition.height;

            this.fireworks.x = this.currentPosition.x;
            this.fireworks.y = this.currentPosition.y;
        }

        this.fireworks.play();
    }
}

export default new FireworksComponent();
